import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, API } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  // One org per account (2026-07-13) — the whole app keys off this single value: nav pill,
  // marketplace-publish lockout, share-to-org, plugin-sync messaging.
  const refreshOrg = useCallback(async () => {
    try {
      const { data } = await api.get("/orgs/mine");
      setOrg((data || []).find((o) => o.membership_status === "active") || null);
    } catch (e) {
      setOrg(null);
    }
  }, []);

  useEffect(() => {
    if (user) refreshOrg();
    else setOrg(null);
  }, [user, refreshOrg]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      console.error("[auth] failed to fetch current user", e);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Native Google OAuth: hand off to the backend, which sets a first-party session
  // cookie and redirects back to `redirect` once Google completes. Several call sites wire
  // this straight to onClick={login}, which passes the DOM event as the first arg — only
  // trust an actual string, everything else (including a SyntheticEvent) falls back to /build.
  const login = (redirect) => {
    const dest = typeof redirect === "string" ? redirect : "/build";
    window.location.href = `${API}/auth/google/login?redirect=${encodeURIComponent(dest)}`;
  };

  // Email/password. The backend sets the first-party session cookie and returns the user.
  const loginEmail = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user);
    return data.user;
  };

  const registerEmail = async (email, password) => {
    const { data } = await api.post("/auth/register", { email, password });
    setUser(data.user);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("[auth] logout request failed (continuing client-side)", e);
    }
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, org, refreshOrg, loading, login, loginEmail, registerEmail, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
